import {
  routeStateQuery,
  scopeToTarget,
  type WebStudioScope,
  WebStudioStaleResponseError,
} from "./boot";
import {
  type WebStudioCloudApiClient,
  type WebStudioCloudApiResponseByOperation,
  webStudioCloudApiOperations,
} from "./generatedClient";
import { makeWebStudioRequest } from "./transport";

/**
 * Command-catalog (CMD) read adapter for the shell command palette. The
 * backend owns the command rows, shortcut labels, native-menu
 * equivalents, and route destination rows; the browser merges them into
 * the palette verbatim and never invents commands, shortcuts, or
 * enablement. Command execution surfaces (replay, export, gc) live in
 * the Commands vertical — this module is read-only.
 */
export type WebStudioCommandCatalogSnapshot =
  WebStudioCloudApiResponseByOperation["readCommandCatalog"];

export interface WebStudioCommandCatalogReadOptions {
  signal?: AbortSignal | undefined;
}

export async function loadCommandCatalog(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  options: WebStudioCommandCatalogReadOptions = {},
): Promise<WebStudioCommandCatalogSnapshot> {
  if (options.signal?.aborted) {
    throw new WebStudioStaleResponseError(
      "Web Studio read_command_catalog read was superseded before dispatch",
    );
  }
  const response = await client.readCommandCatalog(
    makeWebStudioRequest("readCommandCatalog", scopeToTarget(scope), {
      query: routeStateQuery(scope),
    }),
  );
  const contract = webStudioCloudApiOperations.readCommandCatalog.operationContract;
  if (response.operationContract !== contract) {
    throw new WebStudioStaleResponseError(
      "Web Studio read_command_catalog response contract does not match the generated client",
      response.responseIdentity,
    );
  }
  if (options.signal?.aborted) {
    throw new WebStudioStaleResponseError(
      "Web Studio read_command_catalog response was superseded before render",
      response.responseIdentity,
    );
  }
  return response;
}

/** One backend command row narrowed to what the palette renders. */
export interface WebStudioCommandEntry {
  /** The backend row id, palette-unique as delivered. */
  id: string;
  label: string;
  /** Backend shortcut label, only when the catalog delivered one for this row. */
  shortcut?: string | undefined;
  /** Disabled exactly when the backend delivered a disabled reason or state. */
  disabled: boolean;
  /** Backend disabled reason plus diagnostic code, verbatim. */
  disabledReason?: string | undefined;
  /**
   * Backend-named route destination for this command: the row's own
   * `groupId` metadata when delivered, else the matching
   * `routeDestinationRows` entry (`route:<command suffix>`). Undefined
   * means the command has no navigable destination in this catalog.
   */
  destinationHint?: string | undefined;
}

/**
 * Maps the backend command catalog to palette entries. Returns `null`
 * (fail closed) when the backend did not deliver `commandRows`, so the
 * palette lists only client navigation instead of inventing commands.
 */
export function commandEntriesFromCatalog(
  data: WebStudioCommandCatalogSnapshot["data"],
): WebStudioCommandEntry[] | null {
  const commandRows = data.commandRows;
  if (commandRows === undefined) {
    return null;
  }
  const shortcutLabels = data.shortcutLabels ?? {};
  const routeDestinationRows = data.routeDestinationRows ?? [];
  return commandRows.map((row) => {
    const suffix = row.rowId.startsWith("command:")
      ? row.rowId.slice("command:".length)
      : row.rowId;
    const shortcut = shortcutLabels[suffix];
    const disabledReason = row.disabledReason;
    const disabled =
      (disabledReason !== undefined && disabledReason !== "") ||
      row.status === "disabled" ||
      row.status === "unavailable";
    const destinationRow = routeDestinationRows.find((route) => route.rowId === `route:${suffix}`);
    const destinationHint =
      row.groupId ??
      (destinationRow === undefined ? undefined : destinationRow.rowId.slice("route:".length));
    return {
      id: row.rowId,
      label: row.label,
      shortcut: typeof shortcut === "string" && shortcut !== "" ? shortcut : undefined,
      disabled,
      disabledReason: disabled
        ? [disabledReason, row.diagnosticCode]
            .filter((part): part is string => part !== undefined && part !== "")
            .join(" ") || "backend reported this command unavailable"
        : undefined,
      destinationHint,
    };
  });
}
